import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchablePicker, { productMatch, renderProductRow } from "@/components/app/SearchablePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Search } from "lucide-react";
import { toast } from "sonner";

export default function Brands() {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [pf, setPf] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);  // brand row being edited

  const load = async () => {
    const [pr, br] = await Promise.all([api.get("/purchasing/products"), api.get("/purchasing/brands")]);
    setProducts(pr.data); setBrands(br.data);
  };
  useEffect(() => { load(); }, []);

  const productName = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p.name])), [products]);
  const needle = q.trim().toLowerCase();
  const filtered = (pf === "all" ? brands : brands.filter((b) => b.product_id === pf))
    .filter((b) => !needle ||
      (b.name || "").toLowerCase().includes(needle) ||
      (productName[b.product_id] || "").toLowerCase().includes(needle));

  const del = async (b) => {
    if (!window.confirm(`Delete brand "${b.name}"?`)) return;
    try { await api.delete(`/purchasing/brands/${b.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="pv-brands-page">
      <PageHeader
        eyebrow="Purchasing"
        title="Brand Master"
        subtitle="Multiple brands per product. Each vendor can supply any of these."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6" data-testid="pv-new-brand-btn">
                <Plus className="w-4 h-4 mr-2" /> New Brand
              </Button>
            </DialogTrigger>
            {open && <BrandDialog products={products} onDone={() => { setOpen(false); load(); }} />}
          </Dialog>
        }
      />
      <div className="p-4 sm:p-8">
        <div className="mb-4 flex items-center gap-2">
          <div className="relative w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search brands…"
              className="pl-9 h-10 rounded-none"
              data-testid="pv-brands-search"
            />
          </div>
          <div className="w-64">
            <SearchablePicker
              items={[{ id: "all", name: "All products", category: "" }, ...products]}
              value={pf}
              onChange={(v) => setPf(v || "all")}
              placeholder="Filter by product"
              testid="pv-brand-product-filter"
              selectedLabel={(p) => p.name}
              matchItem={productMatch}
              renderRow={renderProductRow}
            />
          </div>
        </div>
        <div className="border border-[#E5E7EB]">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>{["Product", "Brand", "Min Stock", "Status", ""].map((h, i) => (
                <th key={i} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`pv-brand-row-${b.id}`}>
                  <td className="px-4 py-3 text-xs">{productName[b.product_id] || "—"}</td>
                  <td className="px-4 py-3 font-semibold">{b.name}</td>
                  <td className="px-4 py-3 text-xs font-mono">{b.min_stock != null ? b.min_stock : "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className={`px-2 py-0.5 uppercase tracking-widest font-bold text-[9px] ${b.active ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#F3F4F6] text-[#6B7280]"}`}>
                      {b.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing(b)} className="text-[#9CA3AF] hover:text-[#002FA7] mr-3" title="Edit brand" data-testid={`pv-brand-edit-${b.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(b)} className="text-[#9CA3AF] hover:text-[#DC2626]" data-testid={`pv-brand-del-${b.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={5} className="text-center text-sm text-[#9CA3AF] py-12">{q.trim() ? `No brands match "${q}".` : "No brands match the filter."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        {editing && (
          <BrandDialog
            products={products}
            editing={editing}
            onDone={() => { setEditing(null); load(); }}
          />
        )}
      </Dialog>
    </div>
  );
}

function BrandDialog({ products, editing, onDone }) {
  const isEdit = !!editing;
  const [f, setF] = useState({
    product_id: editing?.product_id || products[0]?.id || "",
    name: editing?.name || "",
    active: editing?.active !== false,
    min_stock: editing?.min_stock != null ? String(editing.min_stock) : "",
    notes: editing?.notes || "",
  });
  const submit = async () => {
    if (!f.product_id) { toast.error("Pick a product"); return; }
    if (!f.name.trim()) { toast.error("Brand name required"); return; }
    const payload = { ...f, min_stock: f.min_stock === "" ? null : Number(f.min_stock) };
    try {
      if (isEdit) await api.patch(`/purchasing/brands/${editing.id}`, payload);
      else await api.post("/purchasing/brands", payload);
      toast.success(isEdit ? "Brand updated" : "Brand created");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-md">
      <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">{isEdit ? "Edit Brand" : "New Brand"}</DialogTitle></DialogHeader>
      <div className="space-y-3 pt-2">
        <div>
          <Label className="label-eyebrow">Product *</Label>
          <SearchablePicker
            items={products}
            value={f.product_id}
            onChange={(v) => setF({ ...f, product_id: v })}
            placeholder="Search product…"
            testid="pv-b-product"
            selectedLabel={(p) => p.name}
            matchItem={productMatch}
            renderRow={renderProductRow}
            disabled={isEdit}
          />
        </div>
        <div><Label className="label-eyebrow">Brand Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-none mt-2 h-10" data-testid="pv-b-name" /></div>
        <div>
          <Label className="label-eyebrow">Minimum Stock (for low-stock alerts)</Label>
          <Input type="number" step="0.01" value={f.min_stock} onChange={(e) => setF({ ...f, min_stock: e.target.value })} className="rounded-none mt-2 h-10" placeholder="Leave blank to disable alerts" data-testid="pv-b-min-stock" />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} id="pv-b-active" data-testid="pv-b-active" />
          <Label htmlFor="pv-b-active" className="text-xs uppercase tracking-widest font-bold cursor-pointer">Active</Label>
        </div>
        <Button onClick={submit} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="pv-b-submit">{isEdit ? "Save Changes" : "Create"}</Button>
      </div>
    </DialogContent>
  );
}
